import { Divider } from '@mui/material';
import { navigate } from 'gatsby';
import React, { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ReportContent from '@components/Modal/Content/ReportContent';
import { MoreDrawer, SideDrawer } from '@components/SideDrawer';
import AdminDrawer from '@components/SideDrawer/AdminDrawer';
import ReportDrawer from '@components/SideDrawer/ReportDrawer';

import type { RECORD_UPDATE_TYPES } from '@constants/record-update-types';
import * as ROUTES from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import type { LayoutSetting } from '@layouts/AppLayout/model';

import SideButton from './SideButton';
import { SideStrip } from './SideStripDesktop.style';

const messages = defineMessages({
  dashboard: {
    id: 'sideMenu.dashboard',
    defaultMessage: 'Dashboard',
  },
  clients: {
    id: 'sideMenu.clients',
    defaultMessage: 'Clients',
  },
  records: {
    id: 'sideMenu.records',
    defaultMessage: 'Records',
  },
  contacts: {
    id: 'sideMenu.contacts',
    defaultMessage: 'Contacts',
  },
  routines: {
    id: 'sideMenu.routines',
    defaultMessage: 'Routines',
  },
  healthSummary: {
    id: 'sideMenu.healthSummary',
    defaultMessage: 'Health Summary',
  },
  more: {
    id: 'sideMenu.more',
    defaultMessage: 'More',
  },
  settings: {
    id: 'sideMenu.settings',
    defaultMessage: 'Settings',
  },
  newAccount: {
    id: 'sideMenu.newAccount',
    defaultMessage: 'New Account',
  },
  newPatient: {
    id: 'sideMenu.newPatient',
    defaultMessage: 'New Patient',
  },
  report: {
    id: 'sideMenu.report',
    defaultMessage: 'Report Record',
  },
  tasks: {
    id: 'sideMenu.tasks',
    defaultMessage: 'Tasks',
  },
  timeline: {
    id: 'sideMenu.timeline',
    defaultMessage: 'Timeline',
  },
  backToClients: {
    id: 'sideMenu.backToClients',
    defaultMessage: 'Back to Clients',
  },
  adminManagement: {
    id: 'sideMenu.adminManagement',
    defaultMessage: 'System Management',
  },
  clientOverview: {
    id: 'sideMenu.clientOverview',
    defaultMessage: 'Client Overview',
  },
  patientOverview: {
    id: 'sideMenu.patientOverview',
    defaultMessage: 'Patient Overview',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  appInfo: {
    id: 'sideMenu.appInformation',
    defaultMessage: 'App Information',
  },
  patients: {
    id: 'sideMenu.patients',
    defaultMessage: 'Patients',
  },
  backToPatients: {
    id: 'sideMenu.backToPatients',
    defaultMessage: 'Back to Patients',
  },
  devices: {
    id: 'sideMenu.healthDevices',
    defaultMessage: 'Health Devices',
  },
  shareHistory: {
    id: 'sideMenu.shareHistory',
    defaultMessage: 'Share History',
  },
  accounts: {
    id: 'sideMenu.accounts',
    defaultMessage: 'Accounts',
  },
  assessments: {
    id: 'sideMenu.assessments',
    defaultMessage: 'Assessments',
  },
  alerts: {
    id: 'sideMenu.alerts',
    defaultMessage: 'Alerts',
  },
  orgSettings: {
    id: 'sideMenu.orgSettings',
    defaultMessage: 'Organization Settings',
  },
  discern: {
    id: 'sideMenu.discern',
    defaultMessage: 'Discern',
  },
});

const SideStripDesktop: React.FC<{
  isAdmin?: boolean;
  settings: LayoutSetting;
}> = ({ isAdmin = false, settings = [] }) => {
  const { formatMessage } = useIntl();

  const {
    reset,
    state: { patientView },
  } = useCuration();
  const { closeModal, openModal } = useModal();
  const {
    state: { activeProfile, isCurator, isPractitioner },
  } = useSession();
  const user_id = activeProfile?.user_id;

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openMoreDrawer, setOpenMoreDrawer] = useState(false);
  const [openReportDrawer, setOpenReportDrawer] = useState(false);
  const [openAdminDrawer, setOpenAdminDrawer] = useState(false);

  const recordBtnRef = useRef<HTMLButtonElement | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const reportBtnRef = useRef<HTMLButtonElement | null>(null);
  const adminBtnRef = useRef<HTMLButtonElement | null>(null);

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const handleCloseMoreDrawer = () => {
    setOpenMoreDrawer(false);
  };

  const handleCloseReportDrawer = () => {
    setOpenReportDrawer(false);
  };

  const handleCloseAdminDrawer = () => {
    setOpenAdminDrawer(false);
  };

  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const toggleMoreDrawer = () => {
    setOpenMoreDrawer(!openMoreDrawer);
  };

  const toggleReportDrawer = () => {
    setOpenReportDrawer(!openReportDrawer);
  };

  const toggleAdminDrawer = () => {
    setOpenAdminDrawer(!openAdminDrawer);
  };

  const openReportRecordModal = (type: RECORD_UPDATE_TYPES) => {
    openModal({
      width: 600,
      props: { padding: '0px' },
      children: <ReportContent onSubmit={() => closeModal()} type={type} />,
    });
  };

  const renderListItem = (option: string, index: number) => {
    switch (option) {
      case 'backToClients':
      case 'backToPatients':
        // eslint-disable-next-line no-case-declarations
        const redirect = isPractitioner
          ? ROUTES.PATIENTS
          : isCurator
            ? ROUTES.CLIENTS
            : ROUTES.DASHBOARD;
        return (
          <SideButton
            hoverText={formatMessage(
              isPractitioner
                ? messages.backToPatients
                : isCurator
                  ? messages.backToClients
                  : messages.back
            )}
            iconName="arrow-left"
            key={index}
            onClick={() => {
              reset();
              navigate(redirect);
            }}
            route={redirect}
          />
        );
      case 'home':
        return (
          <SideButton
            hoverText={formatMessage(messages.dashboard)}
            iconName="home"
            key={index}
            onClick={() => {
              navigate(isAdmin ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD);
            }}
            route={isAdmin ? ROUTES.ADMIN_DASHBOARD : ROUTES.DASHBOARD}
          />
        );
      case 'clientOverview':
      case 'patientOverview':
        return (
          <SideButton
            hoverText={formatMessage(
              isPractitioner
                ? messages.patientOverview
                : messages.clientOverview
            )}
            iconName={isPractitioner ? 'hospital-user' : 'user'}
            key={index}
            onClick={() => {
              if (isCurator && user_id) {
                navigate(`${ROUTES.CLIENTS}${user_id}`);
              } else if (isPractitioner && user_id) {
                navigate(`${ROUTES.PATIENTS}${user_id}`);
              }
            }}
            route={
              isCurator && user_id
                ? `${ROUTES.CLIENTS}${user_id}`
                : isPractitioner && user_id
                  ? `${ROUTES.PATIENTS}${user_id}`
                  : ''
            }
          />
        );
      case 'clients':
      case 'patients':
        // eslint-disable-next-line no-case-declarations
        const route = isPractitioner ? ROUTES.PATIENTS : ROUTES.CLIENTS;
        return (
          <SideButton
            hoverText={formatMessage(
              isPractitioner ? messages.patients : messages.clients
            )}
            iconName="users"
            iconNameHover="users"
            key={index}
            onClick={() => {
              navigate(route);
            }}
            route={route}
          />
        );
      case 'accounts':
        return (
          <SideButton
            hoverText={formatMessage(messages.accounts)}
            iconName="sitemap"
            iconNameHover="sitemap"
            key={index}
            onClick={() => {
              navigate(ROUTES.ACCOUNTS);
            }}
            route={ROUTES.ACCOUNTS}
          />
        );
      case 'tasks':
        return (
          <SideButton
            hoverText={formatMessage(messages.tasks)}
            iconName="square-check"
            iconNameHover="square-check"
            key={index}
            onClick={() => {
              navigate(ROUTES.TASKS);
            }}
            route={ROUTES.TASKS}
          />
        );
      case 'records':
        return (
          <SideButton
            hoverText={formatMessage(messages.records)}
            iconName="folder-medical"
            key={index}
            onClick={toggleDrawer}
            ref={recordBtnRef}
            route={ROUTES.RECORDS}
          />
        );
      // case 'contacts':
      //   return (
      //     <SideButton
      //       hoverText={formatMessage(messages.contacts)}
      //       iconName="address-book"
      //       key={index}
      //       onClick={() => {
      //         navigate(ROUTES.CONTACTS);
      //       }}
      //       route={ROUTES.CONTACTS}
      //     />
      //   );
      case 'reminders':
        return (
          <SideButton
            hoverText={formatMessage(messages.routines)}
            iconName="alarm-clock"
            key={index}
            onClick={() => {
              navigate(ROUTES.ROUTINES);
            }}
            route={ROUTES.ROUTINES}
          />
        );
      case 'profile':
        return (
          <SideButton
            hoverText={formatMessage(messages.healthSummary)}
            iconName="clipboard-medical"
            key={index}
            onClick={() => {
              navigate(ROUTES.HEALTH_SUMMARY);
            }}
            route={ROUTES.HEALTH_SUMMARY}
          />
        );
      case 'more':
        return (
          <SideButton
            hoverText={formatMessage(messages.more)}
            iconName="ellipsis-stroke"
            iconNameHover="ellipsis"
            key={index}
            onClick={toggleMoreDrawer}
            ref={moreBtnRef}
          />
        );
      case 'settings':
        return (
          <SideButton
            bottom
            hoverText={formatMessage(messages.settings)}
            iconName="gear"
            key={index}
            onClick={() => {
              navigate(ROUTES.SETTINGS);
            }}
            route={ROUTES.SETTINGS}
          />
        );
      case 'appInformation':
        return (
          <SideButton
            bottom
            hoverText={formatMessage(messages.appInfo)}
            iconName="info"
            key={index}
            onClick={() => {
              navigate(ROUTES.APP_INFORMATION);
            }}
            route={ROUTES.APP_INFORMATION}
          />
        );
      case 'addAccount':
        // eslint-disable-next-line no-case-declarations
        const newAcctRoute = ROUTES.ADMIN_NEW_ACCOUNT;
        return (
          <SideButton
            hoverText={formatMessage(messages.newAccount)}
            iconName="plus"
            key={index}
            onClick={() => {
              navigate(newAcctRoute);
            }}
            route={newAcctRoute}
          />
        );
      case 'report':
        return (
          <SideButton
            hoverText={formatMessage(messages.report)}
            iconName="plus"
            key={index}
            onClick={toggleReportDrawer}
            ref={reportBtnRef}
          />
        );
      case 'timeline':
        return (
          <SideButton
            hoverText={formatMessage(messages.timeline)}
            iconName="timeline"
            iconRotation={-90}
            key={index}
            onClick={() => {
              navigate(ROUTES.TIMELINE);
            }}
            route={ROUTES.TIMELINE}
          />
        );
      case 'adminManagement':
        return (
          <SideButton
            hoverText={formatMessage(messages.adminManagement)}
            iconName="wrench"
            key={index}
            onClick={toggleAdminDrawer}
            ref={adminBtnRef}
            route={ROUTES.ADMIN_MANAGEMENT}
          />
        );
      case 'healthDevices':
        return (
          <SideButton
            hoverText={formatMessage(messages.devices)}
            iconName="watch-fitness"
            key={index}
            onClick={() => {
              navigate(ROUTES.DEVICES);
            }}
            route={ROUTES.DEVICES}
          />
        );
      case 'shareHistory':
        return (
          <SideButton
            hoverText={formatMessage(messages.shareHistory)}
            iconName="share-nodes"
            key={index}
            onClick={() => {
              navigate(ROUTES.SHARE_HISTORY);
            }}
            route={ROUTES.SHARE_HISTORY}
          />
        );
      case 'assessments':
        return (
          <SideButton
            hoverText={formatMessage(messages.assessments)}
            iconName="magnifying-glass-chart"
            key={index}
            onClick={() => {
              navigate(ROUTES.ASSESSMENTS);
            }}
            route={ROUTES.ASSESSMENTS}
          />
        );
      case 'alerts':
        return (
          <SideButton
            hoverText={formatMessage(messages.alerts)}
            iconName="triangle-exclamation"
            key={index}
            onClick={() => {
              navigate(ROUTES.ALERTS);
            }}
            route={ROUTES.ALERTS}
          />
        );
      case 'orgSettings':
        return (
          <SideButton
            hoverText={formatMessage(messages.orgSettings)}
            iconName="users-gear"
            key={index}
            onClick={() => {
              navigate(ROUTES.ORG_MANAGEMENT);
            }}
            route={ROUTES.ORG_MANAGEMENT}
          />
        );
      case 'discern':
        return (
          <SideButton
            hoverText={formatMessage(messages.discern)}
            iconName="brain"
            iconNameHover="brain"
            iconType="fat"
            key={index}
            onClick={() => {
              if (patientView) {
                navigate(ROUTES.DISCERN_JOBS);
              } else {
                navigate(ROUTES.DISCERN);
              }
            }}
            route={ROUTES.DISCERN}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SideDrawer
        btnRef={recordBtnRef}
        isOpen={openDrawer}
        onClose={handleCloseDrawer}
      />
      <MoreDrawer
        btnRef={moreBtnRef}
        isOpen={openMoreDrawer}
        onClose={handleCloseMoreDrawer}
      />
      <ReportDrawer
        btnRef={reportBtnRef}
        isOpen={openReportDrawer}
        onClose={(type) => {
          if (type) {
            openReportRecordModal(type);
          }
          handleCloseReportDrawer();
        }}
      />
      <AdminDrawer
        btnRef={adminBtnRef}
        isOpen={openAdminDrawer}
        onClose={handleCloseAdminDrawer}
      />
      <SideStrip>
        {settings.map((group, groupIndex) => (
          <React.Fragment key={`settings-${groupIndex}`}>
            {group.map((item, itemIndex) => renderListItem(item, itemIndex))}
            {groupIndex !== settings.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </SideStrip>
    </>
  );
};

export default SideStripDesktop;
